import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Assessment from './assessment'
import ConfigCheck from '../../../../components/ConfigCheck'
import BiometricCheck from '../../../../components/BiometricCheck' // Import the new component
import ProctoringGuidelines from '../../../../components/ProctoringGuidelines'
import moment from 'moment'
import Timer from 'components/Timer'
import { useDispatch, useSelector } from 'react-redux' // Added useSelector
import { fetchCommunicationInstructions as fetchInstructions } from '../../actions'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import Bulb from '../../../../components/icons/Bulb'
import { Modal, Button } from 'antd'
import styled from 'styled-components'

import {
  PageContainer,
  ContentWrapper,
  StartButton,
  InstructionCard,
  InstructionHeader,
  InstructionTitle,
  InstructionDuration,
  InstructionDeadline,
  ImportantNotice,
  NoticeText,
  InstructionSectionTitle,
  InstructionList,
  InstructionListItem,
  TakeAssessmentButton,
  ContactInfo,
  ModalOverlay,
  ModalContent,
} from '../../Style'
import { trackAssessmentStarted, trackFullscreenEntered } from '../../../../utils/assessmentEvents'

// Custom Fullscreen Modal Styled Components
const FullscreenModalOverlay = styled(ModalOverlay)`
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 9999;
`

const FullscreenModalContent = styled(ModalContent)`
  max-width: 500px;
  padding: 30px;
  text-align: center;
`

const FullscreenTitle = styled.h2`
  color: #4318ff;
  margin-bottom: 20px;
`

const FullscreenDescription = styled.p`
  margin-bottom: 20px;
  line-height: 1.5;
`

const PrimaryButton = styled(Button)`
  background-color: #4318ff;
  color: white;
  font-weight: bold;
  height: 44px;
  border-radius: 4px;
  border: none;
  padding: 0 24px;
  font-size: 16px;

  &:hover {
    background-color: #3512cc;
    color: white;
  }
`

const CommunicationInstructions = ({
  assessment,
  questions,
  totalQuestions,
  error,
  responses,
  fetchAssessmentQuestions,
  saveQuestionResponse,
  setAssessment,
  fetchCommunicationInstructions,
  isPractice = false,
  onBack,
  enableFullscreen = false,
}) => {
  const [showConfig, setShowConfig] = useState(false)
  const [allChecksComplete, setAllChecksComplete] = useState(false)
  const [verificationStep, setVerificationStep] = useState('config') // 'config' | 'biometric' | 'ready'
  const [isTimerActive, setIsTimerActive] = useState(false)
  const [timerPhase, setTimerPhase] = useState('initial')
  const [countdown, setCountdown] = useState(null)
  const [showAssessment, setShowAssessment] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showFullscreenModal, setShowFullscreenModal] = useState(false)
  const [fullscreenPermissionGranted, setFullscreenPermissionGranted] =
    useState(false)
  const MAX_TAB_SWITCH_WARNINGS = 3
  const dispatch = useDispatch()

  const verificationEnabled = assessment?.allowVerification !== false
  const durationMinutes = Number(assessment?.duration_minutes || 30)

  // Get student details for verification
  const studentDetails = useSelector(state => state.student?.studentDetails) || {};
  const studentId = studentDetails.id || studentDetails.studentId || assessment?.studentId;

  useEffect(() => {
    setTimeout(() => {
      setLoading(false)
    }, 1500)
  }, [])

  useEffect(() => {
    if (assessment?.assessment_set_id) {
      dispatch(fetchInstructions(assessment.assessment_set_id))
    }
  }, [assessment?.assessment_set_id, fetchAssessmentQuestions, dispatch])

  const handleTakeAssessment = () => {
    setShowConfig(true)
    setVerificationStep('config') // Reset step
    setAllChecksComplete(false)
  }

  const handleConfigComplete = () => {
    if (verificationEnabled) {
      // Add small delay before switching to make it feel natural
      setTimeout(() => {
        setVerificationStep('biometric');
      }, 1000);
    } else {
      // Skip biometric, go straight to ready
      setVerificationStep('ready');
      setAllChecksComplete(true);
    }
  }

  const handleBiometricComplete = () => {
    setVerificationStep('ready');
    setAllChecksComplete(true);
  }

  const location = useLocation()
  const navigate = useNavigate()

  // Function to enter fullscreen
  const enterFullscreen = async () => {
    try {
      //console.log('Attempting to enter fullscreen from instruction component');
      const elem = document.documentElement

      if (elem.requestFullscreen) {
        await elem.requestFullscreen()
      } else if (elem.mozRequestFullScreen) {
        await elem.mozRequestFullScreen()
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen()
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen()
      }

      return true
    } catch (error) {
      console.error('Error entering fullscreen:', error)
      return false
    }
  }

  // Custom Fullscreen Permission Modal Component
  const FullscreenPermissionModal = () => {
    const requestFullscreenAccess = async () => {
      try {
        await enterFullscreen()

        // Check if fullscreen was successful
        setTimeout(() => {
          if (
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement
          ) {
            setFullscreenPermissionGranted(true)
            setShowFullscreenModal(false)
            setShowAssessment(true)
          } else {
            alert(
              'You must enable fullscreen to proceed with the assessment. This is required for proctoring.'
            )
          }
        }, 500)
      } catch (error) {
        console.error('Fullscreen error:', error)
        alert(
          'Fullscreen permission is required. Please try again or check your browser settings.'
        )
      }
    }

    return (
      <FullscreenModalOverlay>
        <FullscreenModalContent onClick={e => e.stopPropagation()}>
          <FullscreenTitle>Enable Fullscreen Mode</FullscreenTitle>
          <FullscreenDescription>
            This assessment requires fullscreen mode for proctoring purposes.
            You must enable fullscreen to continue.
          </FullscreenDescription>

          <FullscreenDescription>
            <div style={{ textAlign: 'left', width: '100%' }}>
              <span style={{ marginLeft: '27px' }}>
                <strong>Important:</strong> During this assessment:
              </span>
              <ul style={{ margin: '10px 0' }}>
                <li>Exiting fullscreen is not allowed</li>
                <li>
                  Switching tabs is not allowed (max {MAX_TAB_SWITCH_WARNINGS}{' '}
                  warnings)
                </li>
                <li>Browser navigation is disabled</li>
                <li>Key combinations like Alt+Tab are blocked</li>
              </ul>
              Violations may result in automatic submission of your assessment.
            </div>
          </FullscreenDescription>

          <PrimaryButton onClick={requestFullscreenAccess}>
            Accept & Enter Fullscreen
          </PrimaryButton>
        </FullscreenModalContent>
      </FullscreenModalOverlay>
    )
  }

  // Modify the handleEnterFullscreen function to immediately navigate to assessment for practice mode
  const handleEnterFullscreen = async () => {
    try {
      //console.log('Handling fullscreen entry for assessment type:', isPractice ? 'practice' : 'regular');

      // For practice assessments, we'll enter fullscreen and immediately show assessment
      if (isPractice) {
        // Attempt to enter fullscreen mode
        //console.log('Entering fullscreen for practice assessment');
        try {
          const elem = document.documentElement
          if (elem.requestFullscreen) {
            await elem.requestFullscreen()
          } else if (elem.mozRequestFullScreen) {
            await elem.mozRequestFullScreen()
          } else if (elem.webkitRequestFullscreen) {
            await elem.webkitRequestFullscreen()
          } else if (elem.msRequestFullscreen) {
            await elem.msRequestFullscreen()
          }
          //console.log('Fullscreen entered successfully in instruction.js');
        } catch (fullscreenError) {
          console.error('Fullscreen error in instruction.js:', fullscreenError)
          // Continue even if fullscreen fails
        }

        // Always proceed to show the assessment regardless of fullscreen success
        setFullscreenPermissionGranted(true)
        setShowAssessment(true)
      } else {
        // For regular assessments, enter fullscreen then wait for button click
        const elem = document.documentElement
        if (elem.requestFullscreen) {
          await elem.requestFullscreen()
        } else if (elem.mozRequestFullScreen) {
          await elem.mozRequestFullScreen()
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen()
        } else if (elem.msRequestFullscreen) {
          await elem.msRequestFullscreen()
        }
        setFullscreenPermissionGranted(true)
        setShowFullscreenModal(false)
      }
    } catch (error) {
      console.error('Error in handleEnterFullscreen:', error)
      // Even if fullscreen fails, continue for practice assessments
      if (isPractice) {
        setShowAssessment(true)
      }
    }
  }

  // Update the handleStart function to properly set the current assessment
  const handleStart = async () => {
    // if (!allChecksComplete) return  // checks handled by UI steps now

    if (
      assessment?.assessment_assigned_id &&
      typeof fetchAssessmentQuestions === 'function'
    ) {
      // First, set the current assessment in Redux state before fetching questions
      dispatch({
        type: 'SET_CURRENT_ASSESSMENT_ID',
        payload: {
          assessment_id: assessment.assessment_id || assessment.id,
          assessment_set_id: assessment.assessment_set_id,
          assessment_assigned_id: assessment.assessment_assigned_id,
          allowProctoring: assessment.allowProctoring || false,
          title: assessment.title,
          type: assessment.type,
          isPractice: assessment.isPractice || false,
          isCorporate: assessment.isCorporate,
          AptitudeDiagnosisRemain: assessment.AptitudeDiagnosisRemain,
          CommunicationDiagnosisRemain: assessment.CommunicationDiagnosisRemain,
        },
      })

      const result = await fetchAssessmentQuestions(assessment.assessment_assigned_id)
      if (!result || !result.success) {
        console.error('Failed to fetch assessment questions')
        return
      }
    } else {
      console.error(
        'fetchAssessmentQuestions is not available or assessment_assigned_id is missing',
        {
          hasFunction: typeof fetchAssessmentQuestions === 'function',
          assessmentId: assessment?.assessment_assigned_id,
        }
      )
      return
    }

    // Set flag to indicate it's a practice assessment
    if (isPractice) {
      localStorage.setItem('isPracticeAssessment', 'true')
    }

    setIsTimerActive(true)
    setShowConfig(false)
    setTimerPhase('initial')

    // Start countdown timer
    setTimeout(() => {
      setTimerPhase('countdown')
      setCountdown(3)

      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval)
            setTimerPhase('start')

            setTimeout(() => {
              if (isPractice || enableFullscreen) {
                setShowFullscreenModal(true)
              } else {
                setShowAssessment(true)
              }
            }, 1000)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, 2000)
  }

  // Add a function to close the modal
  const handleCancelConfig = () => {
    setShowConfig(false)
  }

  // Render back button differently based on whether it's practice mode
  const handleBackAction = () => {
    if (isPractice && onBack) {
      onBack()
    } else {
      setAssessment(null)
    }
  }

  // Render function
  if (loading) {
    return <LoadingSpinner />
  }

  if (showAssessment) {
    return (
      <Assessment
        questions={questions}
        totalQuestions={totalQuestions}
        loading={loading}
        error={error}
        responses={responses}
        saveQuestionResponse={saveQuestionResponse}
        startTime={new Date()}
        setAssessment={setAssessment}
        isPractice={isPractice}
        assessment={{ ...assessment, isPractice }}
        fullscreenInitiated={fullscreenPermissionGranted}
        autoStart={isPractice} // Always pass autoStart as true for practice assessments
      />
    )
  }

  return (
    <PageContainer>
      <ContentWrapper>
        {/* Custom Fullscreen Permission Modal */}
        {showFullscreenModal && <FullscreenPermissionModal />}

        {isTimerActive ? (
          <Timer timerPhase={timerPhase} countdown={countdown} />
        ) : (
          <>
            {/* Back button with updated handler */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px',
                cursor: 'pointer',
              }}
              onClick={handleBackAction}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: '8px' }}
              >
                <path
                  d="M19 12H5M5 12L12 19M5 12L12 5"
                  stroke="#4F46E5"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '16px',
                  color: '#4F46E5',
                }}
              >
                Back to Assessments
              </span>
            </div>

            {/* Replace the conditional rendering with a Modal */}
            <Modal
              title={
                verificationStep === 'config' ? "System Check" :
                  verificationStep === 'biometric' ? "Verification" :
                    "Ready to Start"
              }
              open={showConfig}
              onCancel={handleCancelConfig}
              footer={null}
              width={verificationStep === 'biometric' ? '95%' : 500}
              centered
              maskClosable={false}
              style={{ top: 20, maxWidth: verificationStep === 'biometric' ? '700px' : '500px' }}
            >
              {verificationStep === 'config' && (
                <ConfigCheck
                  onAllChecksComplete={handleConfigComplete}
                />
              )}

              {verificationStep === 'biometric' && (
                <BiometricCheck
                  studentId={studentId || 'unknown_student'}
                  onVerificationComplete={handleBiometricComplete}
                  onStartAssessment={handleStart}
                />
              )}

              {verificationStep === 'ready' && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
                  <h3>You are all set!</h3>
                  <p>System check and verification completed successfully.</p>

                  <StartButton
                    allChecksComplete={allChecksComplete}
                    onClick={handleStart}
                    disabled={!allChecksComplete}
                  >
                    Start Assessment
                  </StartButton>
                </div>
              )}
            </Modal>

            <InstructionCard>
              <InstructionHeader>
                <div>
                  <InstructionTitle>Communication Assessment</InstructionTitle>
                  <div
                    style={{
                      fontSize: '30px',
                      fontWeight: '700',
                      color: '#000000',
                    }}
                  >
                    {assessment?.title
                      ? assessment.title.charAt(0).toUpperCase() +
                      assessment.title.slice(1)
                      : ''}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <InstructionDeadline
                    style={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: '22px',
                      lineHeight: '132%',
                      letterSpacing: '0%',
                    }}
                  >
                    Ends on&nbsp;
                    <span
                      style={{
                        color: '#3249D7',
                        marginLeft: '2px',
                        fontWeight: 'bold',
                      }}
                    >
                      {assessment?.endTime
                        ? moment.utc(assessment.endTime).format('Do MMMM, ha [IST]')
                        : '—'}
                    </span>
                  </InstructionDeadline>
                  <InstructionDuration>
                    Duration - {durationMinutes} mins
                  </InstructionDuration>
                </div>
              </InstructionHeader>
            </InstructionCard>

            <InstructionCard>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <div style={{ marginRight: '8px' }}>
                  <Bulb />
                </div>
                <span
                  style={{
                    color: '#4F46E5',
                    fontWeight: '600',
                    fontSize: '26px',
                  }}
                >
                  Important!
                </span>
                <span
                  style={{
                    marginLeft: '8px',
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '26px',
                    lineHeight: '132%',
                    letterSpacing: '0%',
                  }}
                >
                  Instructions
                </span>
              </div>

              <ImportantNotice>
                <NoticeText style={{ fontStyle: 'italic' }}>
                  Assessment has to be taken in one go, please do not exit once
                  started.
                </NoticeText>
              </ImportantNotice>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0',
                }}
              >
                Technical Setup
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  Ensure a stable internet connection.
                </InstructionListItem>
                <InstructionListItem>
                  Use a laptop/desktop equipped with a working webcam,
                  microphone, and speakers.
                </InstructionListItem>
              </InstructionList>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0',
                }}
              >
                Environment
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  Sit in a well-lit, quiet environment with minimal background
                  noise.
                </InstructionListItem>
                <InstructionListItem>
                  Avoid any distractions or interruptions during the assessment.
                </InstructionListItem>
              </InstructionList>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0',
                }}
              >
                Behavior
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  Do not switch tabs or use unauthorized resources.
                </InstructionListItem>
                <InstructionListItem>
                  Stay focused and remain within the assessment interface
                  throughout the session.
                </InstructionListItem>
              </InstructionList>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0',
                }}
              >
                Monitoring
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  The system will monitor your screen, webcam, microphone, and
                  audio for proctoring purposes.
                </InstructionListItem>
                <InstructionListItem>
                  Ensure you are visibly present throughout the assessment.
                </InstructionListItem>
              </InstructionList>

            <ProctoringGuidelines proctoringEnabled={assessment?.allowProctoring !== false} />

              <ContactInfo
                style={{
                  textAlign: 'left',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                  fontStyle: 'italic',
                  marginBottom: '18px',
                  marginLeft: '10px',
                }}
              >
                In case of technical issues, contact the assessment
                co-ordinator.
              </ContactInfo>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '24px',
                }}
              >
                <TakeAssessmentButton
                  onClick={handleTakeAssessment}
                  style={{
                    width: '302px',
                    height: '59px',
                    borderRadius: '8px',
                    fontSize: '18px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    maxWidth: '100%',
                    padding: '0 24px',
                    marginTop: '24px',
                  }}
                >
                  Take assessment
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      marginLeft: '8px',
                      verticalAlign: 'middle',
                      position: 'relative',
                      top: '1px',
                    }}
                  >
                    <path
                      d="M5 12H19M19 12L12 19M19 12L12 5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </TakeAssessmentButton>
              </div>
            </InstructionCard>
          </>
        )}
      </ContentWrapper>
    </PageContainer>
  )
}

export default CommunicationInstructions
